import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom-latest';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Modal, ModalHeader } from 'reactstrap';
import DocumentShow from 'components/UploadedFiles/DocumentShow';
import NoteDetail from 'components/Notes/NoteDetail';
import EmailThreadDetails from 'components/Emails/EmailThreadDetails';
import DisplaySnippet from 'common/DisplaySnippet';
import {
  getAvatar,
  getBadgeIcon,
  getDueTime,
  getTimeOfActivity,
  headerIcon,
  headerName,
  getInvoiceNumber,
} from './activityUtils';
import { LinearProgress } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TooltipStyled } from 'ui/TooltipStyled/TooltipStyled';
import SMSSlideout from './SMSSlideout';

/* TODO: Remove this Modal. We are using modal from material ui and you have to style it accordingly */

const BorderDiv = styled.div`
  .lefCol {
    border-left: 2px solid transparent;
    transition: all ease-in-out 350ms;
    &:hover {
      border-left-color: #00aecc;
    }
  }
  &:focus-visible {
    outline: 2px solid #00aecc;
    outline-offset: 2px;
  }
`;

const ActivityCard = ({
  id,
  user,
  taskType,
  activityType,
  status,
  avatar,
  avatarName,
  subject,
  title,
  percentComplete,
  dueBy,
  documents,
  lastActivityAt,
  description,
  invoiceAmount,
  invoiceNumber,
  isRecurring,
  refreshData,
  uuid,
  entityEmailThreadId,
  dayLabel,
}) => {
  const { requestsPageFeature } = useFlags();
  const [showModal, setShowModal] = useState(false);
  const [showSmsSlideout, setShowSmsSlideout] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    switch (activityType) {
      case 'message':
        navigate(`/inbox/${encodeURIComponent(id)}`, {
          state: { from: 'account' },
        });
        break;
      case 'task':
        navigate(`/task/detail/${id}`, {
          state: { accountsDetail: id, from: location.pathname },
        });
        break;
      case 'invoice':
        navigate(`/billing/recurring/${id}`);
        break;
      case 'wf_request':
        navigate(`/requests/${uuid}`);
        break;
      case 'rfi/request':
        navigate(`/rfi-requests/${uuid}`);
        break;
      case 'document':
      case 'note':
      case 'email':
        setShowModal(true);
        break;
      case 'text_message':
        setShowModal(false);
        setShowSmsSlideout(true);
        break;
      default:
        setShowModal(false);
    }
  };

  const closeModal = () => {
    if (activityType === 'file') {
      refreshData();
    }
    setShowModal(false);
  };

  if (!requestsPageFeature && activityType === 'wf_request') return null;

  return (
    <Box className="ActivityCard">
      <BorderDiv
        role="button"
        tabIndex={0}
        aria-label={`Open ${headerName(activityType).toLowerCase()}${subject || title ? `: ${subject || title}` : ''}`}
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleClick();
          }
        }}
      >
        <Box className="lefCol" sx={{ display: 'grid', gridTemplateColumns: { xs: '32px 32px minmax(0, 1fr)', sm: '48px 36px minmax(0, 1fr) auto' }, gap: { xs: 0.625, sm: 1.25 }, alignItems: 'start', py: 1.25, px: { xs: 0.625, sm: 1.25 }, minWidth: 0 }}>
          <Typography component="span" variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', lineHeight: 1.25 }}>
            {dayLabel && <><span>{dayLabel.split(' ')[0]}</span><br /><Typography component="span" variant="h6" sx={{ color: 'text.primary' }}>{dayLabel.split(' ')[1]}</Typography></>}
          </Typography>
          <Box role="img" aria-label={headerName(activityType).toLowerCase()} sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, bgcolor: 'action.hover', color: 'text.secondary' }}>
            <i className={headerIcon(activityType)} aria-hidden="true" />
          </Box>
          <Stack spacing={0.3125} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.625 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {headerName(activityType)}
              </Typography>
              {taskType && getBadgeIcon(taskType === 'Bill' ? status : taskType)}
            </Box>
            {subject && <DisplaySnippet snippet={subject} type="primary" lines={2} />}
            {title && <DisplaySnippet snippet={title} type={subject ? 'secondary' : 'primary'} lines={2} />}
            {description && activityType !== 'document' && (
              <DisplaySnippet snippet={description} lines={2} />
            )}
            {invoiceNumber && getInvoiceNumber(isRecurring, invoiceNumber)}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.625, minWidth: 0 }} className="text-dark duedateDoc">
              {invoiceAmount && taskType === 'Bill' && (
                <Typography className="text-body" variant="subtitle1" sx={{ fontWeight: 600 }}>
                  ${invoiceAmount}
                </Typography>
              )}
              {dueBy && getDueTime(dueBy, 'due_by')}
              {documents}
              {(activityType === 'wf_request' || activityType === 'rfi/request') &&
                percentComplete != null && (
                  <Box sx={{ width: '12rem', maxWidth: '100%' }}>
                    <TooltipStyled title={`${percentComplete}%`} arrow>
                      <LinearProgress value={percentComplete} variant="determinate" />
                    </TooltipStyled>
                  </Box>
                )}
            </Box>
          </Stack>
          <Stack spacing={0.3125} sx={{ gridColumn: { xs: '3', sm: '4' }, alignItems: 'flex-end', minWidth: 0 }}>
            <Box>
              {getTimeOfActivity(lastActivityAt, 'last_activity')}
            </Box>
            {user && getAvatar({ avatar, avatarName, name: user })}
          </Stack>
        </Box>
      </BorderDiv>
      {showModal && activityType === 'document' && (
        <Modal isOpen={showModal} className="Md-full-width modal-FullH">
          <DocumentShow selectedItem={{ id }} onButtonClose={closeModal} />
        </Modal>
      )}
      {showModal && activityType === 'note' && (
        <NoteDetail
          notesof="activity"
          id={id}
          closeNoteDetailsModal={closeModal}
        />
      )}
      {showModal && activityType === 'email' && (
        <Modal
          isOpen={showModal}
          style={{ height: '80vh' }}
          className="customModal customModal--center customModal--w1150 customModal--nopadding emailModal"
        >
          <ModalHeader className="ModalHeader">
            <button
              type="button"
              onClick={closeModal}
              className="close"
              aria-label="Close"
            >
              <i className="icon-close2" aria-hidden="true" />
            </button>
          </ModalHeader>
          <EmailThreadDetails
            from="linkedEmailAccConTask"
            selectedEmailId={id}
            entityEmailThreadId={entityEmailThreadId}
          />
        </Modal>
      )}
      {showSmsSlideout && activityType === 'text_message' && (
        <SMSSlideout
          threadId={id}
          showSlideout={showSmsSlideout}
          setShowSlideout={setShowSmsSlideout}
        />
      )}
    </Box>
  );
};
export default ActivityCard;
